import React, { useState, useEffect, useMemo } from 'react';
import { getAvailabilityForPlayers } from '../services/playerAvailabilityService';
import PlayerAvailabilityModal from './PlayerAvailabilityModal';
import './PlayerAvailabilityAdminOverlay.css';

/**
 * Collect unique player IDs from matches for a given tournament.
 */
function getPlayerIdsForTournament(matches, tournamentId) {
  const ids = new Set();
  for (const m of matches || []) {
    if (m.tournamentId !== tournamentId) continue;
    if (m.matchType === 'doubles') {
      (m.team1Players || []).forEach((id) => id && ids.add(id));
      (m.team2Players || []).forEach((id) => id && ids.add(id));
    } else {
      if (m.player1Id) ids.add(m.player1Id);
      if (m.player2Id) ids.add(m.player2Id);
    }
    if (m.refereeId) ids.add(m.refereeId);
  }
  return [...ids];
}

export default function PlayerAvailabilityAdminOverlay({
  isOpen,
  onClose,
  tournaments,
  matches,
  getPlayerName,
}) {
  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const [availabilityMap, setAvailabilityMap] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [editPlayerId, setEditPlayerId] = useState(null);

  const selectedTournament = useMemo(
    () => tournaments.find((t) => t.id === selectedTournamentId) || null,
    [tournaments, selectedTournamentId]
  );

  const playerIds = useMemo(
    () => (selectedTournamentId ? getPlayerIdsForTournament(matches, selectedTournamentId) : []),
    [matches, selectedTournamentId]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (tournaments?.length > 0 && !selectedTournamentId) {
      setSelectedTournamentId(tournaments[0].id);
    }
  }, [isOpen, tournaments, selectedTournamentId]);

  useEffect(() => {
    if (!selectedTournamentId || !playerIds.length) {
      setAvailabilityMap(new Map());
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAvailabilityForPlayers(selectedTournamentId, playerIds)
      .then((map) => {
        if (!cancelled) setAvailabilityMap(map);
      })
      .catch((err) => {
        if (!cancelled) setAvailabilityMap(new Map());
        console.error('[PlayerAvailabilityAdminOverlay] load availability', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedTournamentId, playerIds.join(',')]);

  const handleSavedAvailability = () => {
    setEditPlayerId(null);
    if (!selectedTournamentId || !playerIds.length) return;
    getAvailabilityForPlayers(selectedTournamentId, playerIds).then(setAvailabilityMap);
  };

  if (!isOpen) return null;

  const hasAvailability = (playerId) => {
    const a = availabilityMap.get(playerId);
    return a && (a.selectAll || (a.availableSlots && a.availableSlots.length > 0));
  };

  return (
    <>
      <div className="player-availability-admin-overlay" onClick={onClose}>
        <div className="player-availability-admin-modal" onClick={(e) => e.stopPropagation()}>
          <div className="player-availability-admin-header">
            <h2>Player availability</h2>
            <p className="player-availability-admin-subtitle">
              View who has set availability and edit it on their behalf.
            </p>
            <button type="button" className="player-availability-admin-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>

          <div className="player-availability-admin-body">
            {tournaments?.length === 0 ? (
              <p className="player-availability-admin-empty">No tournaments where you are an admin.</p>
            ) : (
              <>
                <div className="player-availability-admin-tournament-row">
                  <label htmlFor="availability-admin-tournament">Tournament</label>
                  <select
                    id="availability-admin-tournament"
                    value={selectedTournamentId}
                    onChange={(e) => setSelectedTournamentId(e.target.value)}
                    className="player-availability-admin-select"
                  >
                    {(tournaments || []).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name || t.id}
                      </option>
                    ))}
                  </select>
                </div>

                {loading ? (
                  <div className="player-availability-admin-loading">Loading…</div>
                ) : playerIds.length === 0 ? (
                  <p className="player-availability-admin-empty">No players in this tournament yet.</p>
                ) : (
                  <div className="player-availability-admin-table-wrap">
                    <table className="player-availability-admin-table">
                      <thead>
                        <tr>
                          <th>Player</th>
                          <th>Availability</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {playerIds.map((playerId) => (
                          <tr key={playerId}>
                            <td>{getPlayerName ? getPlayerName(playerId) : playerId}</td>
                            <td>
                              <span
                                className={
                                  hasAvailability(playerId)
                                    ? 'player-availability-admin-badge has'
                                    : 'player-availability-admin-badge none'
                                }
                              >
                                {hasAvailability(playerId) ? 'Set' : 'Not set'}
                              </span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn-secondary btn-sm"
                                onClick={() => setEditPlayerId(playerId)}
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {editPlayerId && selectedTournament && (
        <PlayerAvailabilityModal
          tournament={selectedTournament}
          playerId={editPlayerId}
          onClose={() => setEditPlayerId(null)}
          onSaved={handleSavedAvailability}
        />
      )}
    </>
  );
}
